import { useAgentAuthStore, useAuthStore } from '../state/auth';
import { captureException, addBreadcrumb } from '../lib/sentry/sentry-utils';
import { ERROR_LEVELS, TAGS } from '../constants/sentry-constants';

const makeAuthenticatedRequest = async (
  url: string,
  options: RequestInit = {}
) => {
  let token = useAuthStore.getState().token;
  if (!token) {
    token = useAgentAuthStore.getState().token;
  }

  const startTime = Date.now();
  const method = options.method || 'GET';

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${token}`,
      },
    });

    const duration = Date.now() - startTime;

    // Add breadcrumb for slow or error responses
    if (duration > 1000 || !response.ok) {
      addBreadcrumb({
        category: 'http',
        type: 'http',
        level: response.ok ? ERROR_LEVELS.WARNING : ERROR_LEVELS.ERROR,
        message: `${method} ${url} - ${response.status}${duration > 1000 ? ' (slow)' : ''}`,
        data: {
          method,
          url,
          statusCode: response.status,
          duration,
        },
      });
    }

    // Capture error responses (skip 401 as it's expected)
    if (!response.ok && response.status !== 401) {
      const errorMessage = `HTTP ${response.status}: ${method} ${url}`;
      captureException(new Error(errorMessage), {
        tags: {
          [TAGS.ERROR_TYPE]: 'fetch_response',
          [TAGS.HTTP_STATUS]: response.status.toString(),
          [TAGS.HTTP_METHOD]: method,
        },
        extra: {
          url,
          statusCode: response.status,
          duration,
        },
      });
    }

    return response;
  } catch (error) {
    // Network error or fetch failure
    addBreadcrumb({
      category: 'http',
      type: 'http',
      level: ERROR_LEVELS.ERROR,
      message: `${method} ${url} - Network Error`,
      data: { method, url, error: error instanceof Error ? error.message : String(error) },
    });

    captureException(error instanceof Error ? error : new Error(String(error)), {
      tags: {
        [TAGS.ERROR_TYPE]: 'fetch_network',
        [TAGS.HTTP_METHOD]: method,
      },
      extra: { url },
    });

    throw error;
  }
};

export default makeAuthenticatedRequest;
